import {
  Box,
  Grid,
  IconButton,
  Step as MuiStep,
  StepLabel,
  Stepper,
  SvgIconTypeMap,
  Typography,
} from '@material-ui/core'
import { OverridableComponent } from '@material-ui/core/OverridableComponent'
import { ClosableDialog } from 'components/controls/dialogs'
import {
  Button,
  ClickableBox,
  CustomTooltip,
  FirstEdIcon,
  LanguageIcon,
  NMeqEXIcon,
} from 'components/facets'
import CardmarketLogo from 'img/cardmarket_blue_logo.png'
import CardTraderLogo from 'img/cardtrader-logo.svg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BulkSettings,
  CardExtraAttribute,
  Competitor,
  Game,
  IdGame,
  PriceStategyTarget,
  PricingStrategy,
  PRICING_BOT_SOURCE,
} from 'shared'
import { CompetitorBox } from '../../CompetitorBox'
import { CreateCompetitorPresenter } from '../CreateCompetitorPresenter'
import { CompetitorParameterPresenter } from './CompetitorParameterPresenter'
import { FinalizePresenter } from './FinalizePresenter'
import { ModifierPresenter } from './ModifierPresenter'

export interface CompetitorFlowPresenterProps {
  newName: string
  onRestart: () => void
  bulkSettings: BulkSettings[]
  competitors: Competitor[]
  onDone: (strategy: PricingStrategy) => void
  onCompetitorDone: (competitor: Competitor) => void
  competitorNewName: string
  activeGame: Game
}

enum Step {
  Source = 1,
  Base,
  Rank,
  Modifier,
  Settings,
  Finalize,
}

const PLACEHOLDER = '—'

const StrategyPreview = ({
  strategy,
  competitor,
  step,
  progress,
}: {
  strategy: PricingStrategy
  competitor: Competitor | undefined
  step: Step
  progress: Step
}): JSX.Element => {
  const { t } = useTranslation()
  const sourceTouched = progress > Step.Source
  const competitorTouched = progress > Step.Base
  const parameterTouched = progress >= Step.Rank && strategy.base.tactic.parameter != null
  const modifierTouched = progress > Step.Modifier
  const nameTouched = progress >= Step.Finalize

  const fieldClass = (touched: boolean, isActive: boolean): string => {
    const classes = ['wizard-preview__field']
    if (!touched) classes.push('wizard-preview__field--untouched')
    if (isActive) classes.push('wizard-preview__field--active')
    return classes.join(' ')
  }

  const sourceLabel = sourceTouched
    ? strategy.source === PRICING_BOT_SOURCE.CARDTRADER
      ? t('common.channels.cardtrader')
      : t('common.channels.cardmarket')
    : PLACEHOLDER

  const isPercent = strategy.modifier.kind === 'percent'
  const modifierValue = Math.abs(strategy.modifier.value)
  const modifierUnit = isPercent ? '%' : modifierValue === 1 ? 'cent' : 'cents'
  const modifierLabel = modifierTouched
    ? `${modifierValue}${isPercent ? modifierUnit : ` ${modifierUnit}`}`
    : PLACEHOLDER
  const lessOrMoreLabel =
    modifierTouched && !isPercent
      ? strategy.modifier.value > 0
        ? t('strategy.less')
        : t('strategy.more')
      : null
  const connector = modifierTouched && isPercent ? t('strategy.ofThe') : t('strategy.thanThe')

  const parameterLabel = parameterTouched ? `${strategy.base.tactic.parameter}.` : PLACEHOLDER
  const parameterTypeLabel = parameterTouched
    ? strategy.base.tactic.parameterType === 'percentile'
      ? t('strategy.percentileCheapest')
      : t('strategy.cheapest')
    : PLACEHOLDER
  const competitorLabel = competitorTouched && competitor ? competitor.name : PLACEHOLDER

  return (
    <div>
      <h6
        className={`wizard-preview__title ${
          step === Step.Finalize ? 'wizard-preview__field--active' : ''
        }`}
      >
        {nameTouched ? strategy.name || PLACEHOLDER : PLACEHOLDER}
      </h6>
      <p className="wizard-preview__sentence">
        <span className={fieldClass(sourceTouched, step === Step.Source)}>{sourceLabel}</span>
        {step >= Step.Modifier && (
          <>
            {' '}
            <span className={fieldClass(modifierTouched, step === Step.Modifier)}>
              {modifierLabel}
            </span>
            {lessOrMoreLabel && (
              <>
                {' '}
                <span className={fieldClass(modifierTouched, step === Step.Modifier)}>
                  {lessOrMoreLabel}
                </span>
              </>
            )}{' '}
            {connector}
          </>
        )}
        {step >= Step.Rank && (
          <>
            {' '}
            <span className={fieldClass(parameterTouched, step === Step.Rank)}>
              {parameterLabel} {parameterTypeLabel}
            </span>{' '}
            {t('strategy.offer')}
          </>
        )}
        {step >= Step.Base && (
          <>
            {' '}
            {t('competitor.from')}{' '}
            <span className={fieldClass(competitorTouched, step === Step.Base)}>
              {competitorLabel}
            </span>
          </>
        )}
      </p>
    </div>
  )
}

interface AttributeSettingProps {
  attribute: keyof Pick<
    PricingStrategy,
    'NMeqEX' | 'disablePlaysetPricing' | 'strictLanguage' | 'strictFirstEd'
  >
  checkBoxText: string
  description: string
  invert?: boolean
  Icon: OverridableComponent<SvgIconTypeMap<unknown, 'svg'>>
}

export const CompetitorFlowPresenter = ({
  newName,
  onRestart,
  competitors,
  onCompetitorDone,
  bulkSettings,
  onDone,
  activeGame,
  competitorNewName,
}: CompetitorFlowPresenterProps): JSX.Element => {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>(Step.Source)
  const [progress, setProgress] = useState<Step>(Step.Source)
  // Clicking a source only selects it (visual feedback); the user advances with
  // the always-visible Next button, which stays disabled until a real choice.
  const [sourceSelected, setSourceSelected] = useState(false)
  const [strategy, setStrategy] = useState<PricingStrategy>({
    _id: '1',
    name: newName,
    disablePlaysetPricing: true,
    NMeqEX: false,
    base: {
      kind: 'articleData',
      tactic: {
        competitor: '',
        parameter: null,
      },
    },
    modifier: {
      kind: 'number',
      value: 1,
    },
    bulkSettingsId: {
      [IdGame.Magic]: '',
      [IdGame.Yugioh]: '',
      [IdGame.Pokemon]: '',
      [IdGame.Vanguard]: '',
      [IdGame.DBS]: '',
      [IdGame.FF]: '',
      [IdGame.WS]: '',
      [IdGame.FAB]: '',
      [IdGame.Digimon]: '',
    },
    source: PRICING_BOT_SOURCE.CARDMARKET,
    target: PriceStategyTarget.sellPrice,
  })
  const [createCompetitorDialogOpen, setCreateCompetitorDialogOpen] = useState(false)
  const chosenCompetitor = competitors.find(({ _id }) => _id === strategy.base.tactic.competitor)

  const [userAttributeChoices, setUserAttributeChoices] = useState<
    Record<AttributeSettingProps['attribute'], null | boolean>
  >({
    NMeqEX: null,
    disablePlaysetPricing: true,
    strictLanguage: null,
    strictFirstEd: activeGame.extraAttr.includes(CardExtraAttribute.FirstEd) ? null : false,
  })

  const stepLabels = [
    t('transaction.channel'),
    t('competitor.title'),
    `${t('competitor.title')} - ${t('common.settings')}`,
    t('strategy.modifier'),
    t('common.settings'),
    t('strategy.name'),
  ]

  const advance = (current: Step): void => {
    setStep(current + 1)
    setProgress(Math.max(current + 1, progress))
  }

  const AttributeSetting = ({
    attribute,
    description,
    checkBoxText,
    invert = false,
    Icon,
  }: AttributeSettingProps): JSX.Element => {
    const active =
      userAttributeChoices[attribute] === null
        ? null
        : invert
        ? !userAttributeChoices[attribute]
        : userAttributeChoices[attribute]

    return (
      <Box>
        <Box paddingLeft={2} display={'flex'} alignItems={'center'}>
          <Typography variant="h6" align={'left'}>
            {checkBoxText}
          </Typography>
          <CustomTooltip title={t('iconOfThisSettings') as string}>
            <IconButton
              size="small"
              style={{ marginLeft: '5px', cursor: 'help' }}
              data-testid={`icon-${attribute}`}
            >
              <Icon color={active ? 'primary' : 'inherit'} />
            </IconButton>
          </CustomTooltip>
        </Box>
        <Box paddingLeft={2} paddingRight={7}>
          <Typography variant="body1">{description}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-evenly" padding={1}>
          <Button
            data-testid={`${attribute}-${invert ? 'no' : 'yes'}-button`}
            variant={active === true ? 'contained' : 'outlined'}
            onClick={() => {
              const strategyCopy = { ...strategy, [attribute]: invert ? false : true }
              setStrategy(strategyCopy)
              setUserAttributeChoices((choices) => ({
                ...choices,
                [attribute]: invert ? false : true,
              }))
            }}
          >
            {t(`strategy.create.${attribute}.yes`)}
          </Button>
          <Button
            data-testid={`${attribute}-${invert ? 'yes' : 'no'}-button`}
            variant={active === false ? 'contained' : 'outlined'}
            onClick={() => {
              const strategyCopy = { ...strategy, [attribute]: invert ? true : false }
              setStrategy(strategyCopy)
              setUserAttributeChoices((choices) => ({
                ...choices,
                [attribute]: invert ? true : false,
              }))
            }}
          >
            {t(`strategy.create.${attribute}.no`)}
          </Button>
        </Box>
      </Box>
    )
  }

  const stepSource = (): JSX.Element => {
    const sources = [
      {
        source: PRICING_BOT_SOURCE.CARDMARKET,
        label: t('common.channels.cardmarket'),
        logo: CardmarketLogo,
      },
      {
        source: PRICING_BOT_SOURCE.CARDTRADER,
        label: t('common.channels.cardtrader'),
        logo: CardTraderLogo,
      },
    ]
    return (
      <>
        <h6 className="mb-2">{t('strategy.create.sourceDescription')}</h6>
        <div className="mx-auto d-flex flex-column gap-4 wizard-source-options mt-5">
          {sources.map(({ source, label, logo }) => (
            <div
              key={source}
              data-testid={`box-${source}`}
              onClick={() => {
                setStrategy({ ...strategy, source })
                setSourceSelected(true)
              }}
              className={`d-flex flex-row justify-content-between align-items-center sales-channel-box ${
                sourceSelected && strategy.source === source ? 'sales-channel-box--active' : ''
              }`}
            >
              <h6 className="m-0">{label}</h6>
              <img src={logo} alt={logo} />
            </div>
          ))}
        </div>
      </>
    )
  }

  const stepBase = (): JSX.Element => (
    <>
      <h6 className="m-0 p-0 mb-2">{t('strategy.create.competitorDescription')}</h6>
      <p className="mb-3">{t('strategy.create.competitorDescription2')}</p>
      <Grid container>
        {competitors
          .filter(
            (comp) =>
              (comp.source ?? PRICING_BOT_SOURCE.CARDMARKET) ===
              (strategy.source ?? PRICING_BOT_SOURCE.CARDMARKET)
          )
          .map((comp) => (
            <Grid xs={12} key={comp._id} item data-testid={`competitor-box-${comp.name}`}>
              <ClickableBox
                onClick={() => {
                  const strategyCopy = { ...strategy }
                  strategyCopy.base.tactic.competitor = comp._id
                  setStrategy(strategyCopy)
                }}
              >
                <CompetitorBox competitor={comp} selected={chosenCompetitor?._id === comp._id} />
              </ClickableBox>
            </Grid>
          ))}
      </Grid>
    </>
  )

  const stepRank = (): JSX.Element => (
    <CompetitorParameterPresenter
      strategy={strategy}
      chosenCompetitor={chosenCompetitor!}
      onChange={(parameterType, parameterValue) => {
        const strategyCopy = { ...strategy }
        if (parameterType === null) {
          strategyCopy.base.tactic.parameter = null
        } else {
          strategyCopy.base.tactic.parameter = parameterValue
          strategyCopy.base.tactic.parameterType = parameterType
        }
        setStrategy(strategyCopy)
      }}
    />
  )

  const stepModifier = (): JSX.Element => (
    <ModifierPresenter
      bulkSettings={bulkSettings}
      strategy={strategy}
      onChange={(s) => setStrategy(s)}
    />
  )

  const stepSettings = (): JSX.Element => (
    <>
      <AttributeSetting
        Icon={NMeqEXIcon}
        attribute="NMeqEX"
        description={t('strategy.create.NMeqEXDescription')}
        checkBoxText={t('strategy.create.NMeqEXTitle')}
      />
      {!!activeGame.extraAttr.includes(CardExtraAttribute.FirstEd) && (
        <AttributeSetting
          Icon={FirstEdIcon}
          attribute="strictFirstEd"
          description={t('strategy.create.strictFirstEdDescription')}
          checkBoxText={t('strategy.create.strictFirstEdTitle')}
        />
      )}
      <AttributeSetting
        Icon={LanguageIcon}
        attribute="strictLanguage"
        description={t('strategy.create.strictLanguageDescription')}
        checkBoxText={t('strategy.create.strictLanguageTitle')}
      />
    </>
  )

  const stepFinalize = (): JSX.Element => (
    <FinalizePresenter strategy={strategy} setStrategy={setStrategy} />
  )

  const fullWidthStep = step === Step.Modifier || step === Step.Settings

  const nextConfig = ((): {
    onClick: () => void
    isLast?: boolean
    disabled?: boolean
  } | null => {
    switch (step) {
      case Step.Source:
        return {
          onClick: () => advance(Step.Source),
          disabled: !sourceSelected,
        }
      case Step.Base:
        return {
          onClick: () => advance(Step.Base),
          disabled: !strategy.base.tactic.competitor,
        }
      case Step.Rank:
        return {
          onClick: () => advance(Step.Rank),
          disabled: !strategy.base.tactic.parameterType,
        }
      case Step.Modifier:
        return { onClick: () => advance(Step.Modifier) }
      case Step.Settings:
        return {
          onClick: () => advance(Step.Settings),
          disabled: Object.keys(userAttributeChoices).some(
            (key) => userAttributeChoices[key as keyof typeof userAttributeChoices] === null
          ),
        }
      case Step.Finalize:
        return {
          onClick: () => onDone(strategy),
          isLast: true,
          disabled: strategy.name.trim() === '',
        }
      default:
        return null
    }
  })()

  return (
    <div className="p-3 mb-5">
      <button
        type="button"
        onClick={onRestart}
        className="wizard-back mb-2"
        data-testid="restart-btn-competition-strategy"
      >
        ← {t('strategy.create.changeType')}
      </button>
      <Stepper activeStep={step - 1} alternativeLabel className="wizard-stepper">
        {stepLabels.map((label, index) => {
          const stepNumber = index + 1
          const canNavigate = progress >= stepNumber
          return (
            <MuiStep key={label}>
              <StepLabel
                style={{ cursor: canNavigate ? 'pointer' : 'default' }}
                onClick={() => canNavigate && setStep(stepNumber)}
                data-testid={`step-${index}`}
              >
                {label}
              </StepLabel>
            </MuiStep>
          )
        })}
      </Stepper>
      <Grid container spacing={4} className="h-100 mt-3" data-testid="competition-strategy-box">
        <Grid item xs={fullWidthStep ? 12 : 7}>
          <div className="pt-2">
            {step === Step.Source && stepSource()}
            {step === Step.Base && stepBase()}
            {step === Step.Rank && stepRank()}
            {step === Step.Modifier && stepModifier()}
            {step === Step.Settings && stepSettings()}
            {step === Step.Finalize && stepFinalize()}
          </div>
        </Grid>
        {!fullWidthStep && (
          <Grid item xs={5} className="d-flex flex-column justify-content-start mt-4 h-100">
            <div className="wizard-preview">
              <div className="wizard-preview__label">{t('competitor.create.previewLabel')}</div>
              <StrategyPreview
                strategy={strategy}
                competitor={chosenCompetitor}
                step={step}
                progress={progress}
              />
            </div>
          </Grid>
        )}
      </Grid>
      {nextConfig && (
        <div className="d-flex justify-content-end mt-3">
          <Button
            disabled={nextConfig.disabled}
            onClick={nextConfig.onClick}
            variant="contained"
            data-testid="next-btn"
          >
            {t(`common.${nextConfig.isLast ? 'done' : 'next'}`)}
          </Button>
        </div>
      )}
      <ClosableDialog
        maxWidth="lg"
        open={createCompetitorDialogOpen}
        onClose={() => setCreateCompetitorDialogOpen(false)}
      >
        <CreateCompetitorPresenter
          onDone={(competitor) => {
            onCompetitorDone(competitor)
            setCreateCompetitorDialogOpen(false)
          }}
          key={String(createCompetitorDialogOpen)}
          onRestart={() => setCreateCompetitorDialogOpen(false)}
          newName={competitorNewName}
        />
      </ClosableDialog>
    </div>
  )
}
